import React, { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Card, Typography } from "@mui/material";
import { DataTable } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import { GET_PLATFORM_REWARDS_BY_COMPANY } from "graphql/platform";
import { formatCashFull } from "utils/formatCash";
import type { PlatformRewardsCompany, PlatformRewardsPool } from "./types";

interface RewardsByCompanyTableProps {
  pool: PlatformRewardsPool | null;
}

const RewardsByCompanyTable: React.FC<RewardsByCompanyTableProps> = ({
  pool,
}) => {
  const { t } = useTranslation();
  const { data, loading } = useQuery<{
    platformRewardsByCompany: PlatformRewardsCompany[];
  }>(GET_PLATFORM_REWARDS_BY_COMPANY, {
    variables: {
      platformAccountId: pool?.platformAccountId,
      currency: pool?.currency,
    },
    skip: !pool,
    fetchPolicy: "network-only",
  });

  const rows = data?.platformRewardsByCompany ?? [];
  const columns: DataColumn<PlatformRewardsCompany>[] = useMemo(
    () => [
      {
        id: "company",
        header: t("Account"),
        cell: (row) => row.businessLegalName ?? row.companyId,
      },
      {
        id: "pending",
        header: t("Pending"),
        align: "right",
        cell: (row) => formatCashFull(row.pending, row.currency),
      },
      {
        id: "available",
        header: t("Available"),
        align: "right",
        cell: (row) => formatCashFull(row.available, row.currency),
      },
      {
        id: "outgoing",
        header: t("Outgoing"),
        align: "right",
        cell: (row) => formatCashFull(row.outgoing, row.currency),
      },
      {
        id: "withheld",
        header: t("Withheld"),
        align: "right",
        cell: (row) => formatCashFull(row.withheld, row.currency),
      },
    ],
    [t],
  );

  return (
    <Card
      variant="outlined"
      sx={{
        position: "relative",
        minWidth: 0,
        padding: "var(--space-5)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h4">{t("Rewards by account")}</Typography>
      <DataTable<PlatformRewardsCompany>
        columns={columns}
        rows={rows}
        getRowKey={(row) => row.companyId}
        loading={loading}
        maxHeight={350}
        emptyState={t("No accounts have earned rewards in this pool")}
        stickyHeader
      />
    </Card>
  );
};

export default RewardsByCompanyTable;
